import type { Seed } from "./types";

const NON_ZERO_FALLBACK = 0x6d2b79f5;

export function hashSeed(seed: Seed): number {
  if (typeof seed === "number") {
    const normalized = seed >>> 0;
    return normalized === 0 ? NON_ZERO_FALLBACK : normalized;
  }

  let hash = 0x811c9dc5;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0 || NON_ZERO_FALLBACK;
}

export function mixSeed(seed: number, label: string): number {
  let hash = seed >>> 0;
  for (let index = 0; index < label.length; index += 1) {
    hash ^= label.charCodeAt(index);
    hash = Math.imul(hash, 0x45d9f3b);
    hash ^= hash >>> 16;
  }
  return hash >>> 0 || NON_ZERO_FALLBACK;
}

/** Deterministic xorshift32 generator. State is intentionally serializable. */
export class SeededRng {
  private stateValue: number;

  public constructor(seed: Seed) {
    this.stateValue = hashSeed(seed);
  }

  public static fromState(state: number): SeededRng {
    const rng = new SeededRng(state);
    rng.stateValue = state >>> 0 || NON_ZERO_FALLBACK;
    return rng;
  }

  public get state(): number {
    return this.stateValue >>> 0;
  }

  public nextUint32(): number {
    let value = this.stateValue >>> 0;
    value ^= value << 13;
    value ^= value >>> 17;
    value ^= value << 5;
    this.stateValue = value >>> 0 || NON_ZERO_FALLBACK;
    return this.stateValue;
  }

  public nextFloat(): number {
    return this.nextUint32() / 0x100000000;
  }

  public nextInt(maxExclusive: number): number {
    if (!Number.isInteger(maxExclusive) || maxExclusive <= 0) {
      throw new RangeError("maxExclusive must be a positive integer");
    }

    const limit = 0x100000000 - (0x100000000 % maxExclusive);
    let value = this.nextUint32();
    while (value >= limit) {
      value = this.nextUint32();
    }
    return value % maxExclusive;
  }

  public pick<T>(values: readonly T[]): T {
    if (values.length === 0) {
      throw new RangeError("Cannot pick from an empty array");
    }
    return values[this.nextInt(values.length)] as T;
  }

  public fork(label: string): SeededRng {
    return SeededRng.fromState(mixSeed(this.state, label));
  }
}

export function createRng(seed: Seed): SeededRng {
  return new SeededRng(seed);
}

export const SeededRandom = SeededRng;
